// Holds the page loader until the route has resolved AND the outgoing page has fully exited,
// so stray exit animations inside a page can never reveal it mid-transition.

export const RELEASE_DELAY_MS = 100; // Delay for scroll restoration
export const MAX_HOLD_MS = 2000; // Failsafe so a runaway exit can't keep the loader up

export function createPageTransitionGate(setLoading: (loading: boolean) => void) {
  let targetKey: string | null = null;
  let waitingForRoute = false;
  let waitingForExit = false;
  // Exits still owed by earlier swaps, so they aren't counted toward the current route
  let staleExits = 0;
  let holdTimer: ReturnType<typeof setTimeout> | undefined;
  let releaseTimer: ReturnType<typeof setTimeout> | undefined;

  const clearTimers = () => {
    clearTimeout(holdTimer);
    clearTimeout(releaseTimer);
  };

  const tryRelease = () => {
    if (waitingForRoute || waitingForExit) return;
    clearTimers();
    targetKey = null;
    releaseTimer = setTimeout(() => setLoading(false), RELEASE_DELAY_MS);
  };

  return {
    // swapsPage: false when the route keeps the same page component mounted (no exit will fire, ie /inductees/ -> /inductees/classes/)
    start(routeKey: string, swapsPage: boolean) {
      if (!waitingForRoute && waitingForExit) staleExits++;
      clearTimers();
      targetKey = routeKey;
      waitingForRoute = true;
      waitingForExit = swapsPage;
      setLoading(true);
    },
    // An in-flight route was superseded by one back to the page still mounted, so no swap will happen
    retarget(routeKey: string) {
      if (!waitingForRoute) return;
      targetKey = routeKey;
      waitingForExit = false;
    },
    routeComplete(routeKey: string) {
      if (!waitingForRoute || routeKey !== targetKey) return;
      waitingForRoute = false;
      if (waitingForExit) {
        holdTimer = setTimeout(() => {
          staleExits = 0;
          waitingForExit = false;
          tryRelease();
        }, MAX_HOLD_MS);
      }
      tryRelease();
    },
    exitComplete() {
      if (staleExits > 0) {
        staleExits--;
        return;
      }
      if (!waitingForExit) return;
      waitingForExit = false;
      tryRelease();
    },
    fail() {
      if (!waitingForRoute && !waitingForExit) return;
      waitingForRoute = false;
      waitingForExit = false;
      tryRelease();
    },
  };
}
